import type { ReactNode } from "react";
import { useMemo } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { fontFamily, layout, space } from "@passfirst/ui";

import { Lockup } from "./lockup";
import { type Colors, useColors } from "./theme";
import { useResponsive } from "./use-responsive";

interface Props {
  /** Navy-panel headline (web/wide only), e.g. "Welcome back." */
  panelTitle: string;
  /** Navy-panel supporting copy (web/wide only). */
  panelBody: string;
  /** Optional 1-based step (renders the stepper in the panel). */
  step?: number;
  /**
   * Total steps in the flow (stepper renders this many dots). Defaults to 3
   * for back-compat; pass the real count. A flow of 1 step hides the stepper
   * entirely — a lone dot communicates no progress.
   */
  totalSteps?: number;
  /** Optional shield/trust line at the foot of the panel. */
  shieldLine?: string;
  /**
   * Optional sticky action (e.g. a PinnedCtaFooter). On narrow it pins
   * below the scroll; on wide it pins to the foot of the content pane.
   */
  footer?: ReactNode;
  /** Content column width (default formMaxWidth 360). Pickers want more. */
  contentWidth?: number;
  /** Renders a back chevron (top-left) when set — preserves step-back nav. */
  onBack?: () => void;
  /**
   * Screen body. Receives `isWide` so the caller can drop a redundant
   * heading on wide (the panel carries it) or show a lockup on narrow.
   */
  children: (isWide: boolean) => ReactNode;
}

// Web "brand-split" frame (design_handoff_2): on wide (≥900) a 42% navy
// panel sits beside the 58% form/content; on narrow it collapses to a
// single centered column (with a lockup at the top). The navy panel is a
// brand surface — full navy in both themes, white lockup + white text.
export function BrandSplit({
  panelTitle,
  panelBody,
  step,
  totalSteps = 3,
  shieldLine,
  footer,
  contentWidth,
  onBack,
  children,
}: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { isWide } = useResponsive();
  const insets = useSafeAreaInsets();
  const width = contentWidth ?? layout.formMaxWidth;

  const backButton = onBack ? (
    <Pressable
      onPress={onBack}
      accessibilityRole="button"
      accessibilityLabel="Back"
      style={styles.back}
    >
      <Text style={styles.backText}>‹ Back</Text>
    </Pressable>
  ) : null;

  if (!isWide) {
    return (
      <View style={[styles.narrowOuter, { paddingTop: insets.top }]}>
        {backButton ? <View style={styles.narrowBack}>{backButton}</View> : null}
        <ScrollView contentContainerStyle={styles.narrowContent}>
          <View style={styles.narrowLockup}>
            <Lockup size={30} />
          </View>
          <View style={{ width, maxWidth: "100%" }}>{children(false)}</View>
        </ScrollView>
        {footer}
      </View>
    );
  }

  return (
    <View style={styles.row}>
      <View style={styles.panel}>
        <Lockup size={28} onDark />
        <View style={styles.panelTextBlock}>
          <Text style={styles.panelTitle}>{panelTitle}</Text>
          <Text style={styles.panelBody}>{panelBody}</Text>
        </View>
        {step && totalSteps > 1 ? (
          <View style={styles.stepper}>
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((n) => (
              <View
                key={n}
                style={[
                  styles.stepDot,
                  n === step ? styles.stepDotActive : styles.stepDotIdle,
                ]}
              >
                <Text
                  style={[styles.stepNum, n === step && styles.stepNumActive]}
                >
                  {n}
                </Text>
              </View>
            ))}
          </View>
        ) : null}
        {shieldLine ? <Text style={styles.shield}>{shieldLine}</Text> : null}
      </View>
      <View style={[styles.main, { paddingTop: insets.top }]}>
        {backButton ? <View style={styles.wideBack}>{backButton}</View> : null}
        <ScrollView contentContainerStyle={styles.mainContent}>
          <View style={{ width, maxWidth: "100%" }}>{children(true)}</View>
        </ScrollView>
        {footer}
      </View>
    </View>
  );
}

const PANEL_NAVY = "#0F2A4A";

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    narrowOuter: { flex: 1, backgroundColor: colors.background },
    narrowBack: { paddingTop: space[6], paddingHorizontal: space[5] },
    wideBack: { position: "absolute", top: space[6], left: space[6], zIndex: 2 },
    back: { paddingVertical: space[2], paddingHorizontal: space[2] },
    backText: {
      fontFamily: fontFamily.semiBold,
      fontSize: 15,
      color: colors.textSecondary,
    },
    narrowContent: {
      flexGrow: 1,
      alignItems: "center",
      // Top-align: with `justifyContent: "center"` the top of taller-than-
      // viewport content is pushed above the scroll origin and clipped
      // (short windows, tablet portrait, keyboard open). `flexGrow: 1`
      // keeps the column filling the viewport so the content still reads
      // from the top.
      padding: space[6],
    },
    narrowLockup: { marginBottom: space[6] },
    row: { flex: 1, flexDirection: "row", backgroundColor: colors.background },
    panel: {
      flexBasis: "42%",
      flexGrow: 0,
      backgroundColor: PANEL_NAVY,
      paddingHorizontal: space[10],
      paddingVertical: space[12],
      justifyContent: "center",
      gap: space[8],
    },
    panelTextBlock: { gap: space[4] },
    panelTitle: {
      fontFamily: fontFamily.extraBold,
      fontSize: 34,
      lineHeight: 40,
      letterSpacing: -0.6,
      color: "#FFFFFF",
    },
    panelBody: {
      fontFamily: fontFamily.regular,
      fontSize: 17,
      lineHeight: 26,
      color: "#9FB2CC",
      maxWidth: 38 * 8,
    },
    stepper: { flexDirection: "row", gap: space[3] },
    stepDot: {
      width: 30,
      height: 30,
      borderRadius: 15,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1.5,
    },
    stepDotActive: { backgroundColor: colors.action, borderColor: colors.action },
    stepDotIdle: { backgroundColor: "transparent", borderColor: "#3A5170" },
    stepNum: {
      fontFamily: fontFamily.bold,
      fontSize: 13,
      color: "#6E8AAC",
    },
    stepNumActive: { color: "#FFFFFF" },
    shield: {
      fontFamily: fontFamily.regular,
      fontSize: 13,
      color: "#7F97B8",
    },
    main: { flex: 1 },
    mainContent: {
      flexGrow: 1,
      alignItems: "center",
      // Top-align: see narrowContent — centering clips the top of content
      // taller than the viewport (it scrolls above the origin, unreachable).
      padding: space[10],
    },
  });
}
